<template>
  <div class="wg-kpi" :class="'wg-kpi-' + (config.size || 'lg')">
    <div class="wg-kpi-value" :class="colorClass">{{ display }}</div>
    <div class="wg-kpi-label">{{ config.label || label }}</div>
    <div v-if="config.caption" class="wg-kpi-caption">{{ config.caption }}</div>
  </div>
</template>

<script>
export default {
  name: 'KpiWidget',
  props: {
    inputs: { type: Object, default: () => ({}) },
    config: { type: Object, default: () => ({}) },
    width: { type: Number, default: 0 },
    height: { type: Number, default: 0 },
  },
  computed: {
    rows() {
      return (this.inputs.rows || {}).rows || [];
    },
    columns() {
      return (this.inputs.rows || {}).columns || [];
    },
    valueField() {
      return this.config.value_field || this.columns.find((c) => /^VALUE$/i.test(c)) || this.columns[0];
    },
    label() {
      return this.valueField || '';
    },
    value() {
      const rows = this.rows;
      if (!rows.length) return null;
      const num = (row, field) => {
        const v = Number(row[field]);
        return Number.isFinite(v) ? v : 0;
      };
      const mode = this.config.aggregate || 'sum';
      if (mode === 'count') return rows.length;
      if (mode === 'ratio') {
        const n = rows.reduce((a, r) => a + num(r, this.config.numerator_field || 'NUMERATOR'), 0);
        const d = rows.reduce((a, r) => a + num(r, this.config.denominator_field || 'DENOMINATOR'), 0);
        return d ? n / d : null;
      }
      const values = rows.map((r) => num(r, this.valueField));
      if (mode === 'avg') return values.reduce((a, b) => a + b, 0) / values.length;
      if (mode === 'last') return values[values.length - 1];
      if (mode === 'max') return Math.max(...values);
      if (mode === 'min') return Math.min(...values);
      return values.reduce((a, b) => a + b, 0);
    },
    display() {
      const v = this.value;
      if (v === null || v === undefined) return '—';
      const decimals = this.config.decimals ?? (this.config.format === 'integer' ? 0 : 1);
      if (this.config.format === 'percent') return `${(v * 100).toFixed(decimals)}%`;
      if (this.config.format === 'currency') return v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: decimals });
      return v.toLocaleString(undefined, { maximumFractionDigits: decimals });
    },
    colorClass() {
      return `wg-kpi-${this.config.color || 'default'}`;
    },
  },
};
</script>

<style scoped>
.wg-kpi { height: 100%; display: flex; flex-direction: column; justify-content: center; padding: 12px 16px; }
.wg-kpi-value { font-weight: 700; font-variant-numeric: tabular-nums; color: #1a1918; line-height: 1.05; }
.wg-kpi-sm .wg-kpi-value { font-size: 22px; }
.wg-kpi-md .wg-kpi-value { font-size: 30px; }
.wg-kpi-lg .wg-kpi-value { font-size: 40px; }
.wg-kpi-label { margin-top: 6px; font-size: 12.5px; color: #56534d; }
.wg-kpi-caption { margin-top: 2px; font-size: 11.5px; color: #8b877f; }
.wg-kpi-risk { color: #C0392B; }
.wg-kpi-stable { color: #2F6B49; }
.wg-kpi-attention { color: #B08A3A; }
.wg-kpi-info { color: #3E5C8A; }
.wg-kpi-accent { color: #EF6C3B; }
</style>
